import React, { useCallback, useEffect, useMemo, useState } from "react";
import SidebarMenu from "../../Components/Sidebar/Sidebar";
import { Row, Col, Modal, Button } from "react-bootstrap";
import { Input, Table } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchPatientSearch } from "../../Components/Helpers/API/PatientApi/PatientApi";
import { queueToCSR, markQueueDone } from "../../Helpers/ApiCalls/Dashboard/DashboardApi";
// import { checkPhilhealthATC } from "../../Components/Helpers/API/PhilhealthApi/PhilhealthApi"; // Commented out - ATC not needed per client
import "../../Components/Commons/Commons.css";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import BtnSave from "../../Components/Commons/Buttons/BtnSave";
import BtnDelete from "../../Components/Commons/Buttons/BtnDelete";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import { formatDate } from "../../Utils/dateFormat";

// Helper function to calculate age in months from birthdate
const getAgeInMonths = (birthdate) => {
  if (!birthdate) return 0;
  const today = new Date();
  const birth = new Date(birthdate);
  const months =
    (today.getFullYear() - birth.getFullYear()) * 12 +
    (today.getMonth() - birth.getMonth());
  return months;
};

function ConsultPatientSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // ATC Modal states - Commented out per client request (ATC not needed)
  // const [showAtcModal, setShowAtcModal] = useState(false);
  // const [atc, setAtc] = useState("");
  // const [atcLoading, setAtcLoading] = useState(false);
  // const [selectedPatient, setSelectedPatient] = useState(null);

  // Laboratory confirmation modal states
  const [showLabModal, setShowLabModal] = useState(false);
  const [labLoading, setLabLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Check if this is a Laboratory flow
  const isLaboratory = useMemo(() => {
    const st = (location && location.state) || {};
    return st?.isLaboratory || false;
  }, [location]);

  const queueInfo = useMemo(() => {
    const st = (location && location.state) || {};
    if (st?.queueId && st?.queueNo) {
      return { id: st.queueId, queue_no: st.queueNo };
    }
    try {
      const fromLocal = JSON.parse(localStorage.getItem("queue_id") || "null");
      if (fromLocal?.id && fromLocal?.queue_no) return fromLocal;
    } catch (e) {}
    return { id: "", queue_no: "" };
  }, [location]);

  async function runSearch(nextPage = 1, keyword = "") {
    setLoading(true);
    try {
      const resp = await fetchPatientSearch({
        id: "",
        page: nextPage,
        search: keyword,
      });
      if (resp?.data) {
        setPatients(resp.data.data || []);
        setTotal(resp.data.total_size || 0);
        setPage(nextPage);
      } else {
        setPatients([]);
        setTotal(0);
      }
    } catch (e) {
      setPatients([]);
      setTotal(0);
    }
    setLoading(false);
  }

  const debouncedSearch = useCallback(
    debounce((nextValue) => runSearch(1, nextValue), 500),
    []
  );

  useEffect(() => {
    if (search.length >= 3) {
      debouncedSearch(search);
    } else {
      setPatients([]);
      setTotal(0);
      setPage(1);
    }
  }, [search, debouncedSearch]);

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, r) =>
        `${r.last_name || ""}, ${r.first_name || ""} ${
          r.middle_name || ""
        }`.trim(),
    },
    {
      title: "Sex",
      dataIndex: "sex",
      key: "sex",
      width: 90,
    },
    {
      title: "Birthdate",
      dataIndex: "birthdate",
      key: "birthdate",
      width: 140,
      render: (value) => formatDate(value, value),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, r) => {
        const handleSelect = () => {
          if (isLaboratory) {
            // Show confirmation modal for Laboratory flow
            setSelectedPatient(r);
            setShowLabModal(true);
          } else {
            // Navigate directly to vitals page (ATC removed per client request)
            navigate(`/patients/vitals/${r.id}`, {
              state: {
                fromConsult: isLaboratory ? false : true,
                queueId: queueInfo.id,
                queueNo: queueInfo.queue_no,
                patient_sex: r.sex?.toUpperCase() || "",
                months: getAgeInMonths(r.birthdate),
              },
            });
          }
        };
        return (
          <Button className="action-btn save" size="sm" onClick={handleSelect}>
            Select
          </Button>
        );
      },
    },
  ];

  return (
    <div className="App">
      <SidebarMenu active={"Patients"} />
      <div className="col pt-4 scrollable-container">
        <Row className="px-5 mb-4">
          <Col className="col font-big title-2 all-patient all-patients-title">
            {isLaboratory ? "Select Patient for Laboratory" : "Select Patient for Consultation"}
          </Col>
        </Row>
        <div className="row ms-5">
          <div className="col-5 pe-4 me-2 mb-2 half-width">
            <Input
              placeholder="Search by name"
              allowClear
              size="middle"
              className="responsive-header-input"
              prefix={<FontAwesomeIcon icon={faSearch} style={{ marginRight: 8 }} />}
              onChange={(e) => {
                const v = e.target.value;
                setSearch(v);
              }}
            />
          </div>
        </div>
        <div className="row justify-content-center">
          <div className="col-sm-11 all-table custom-table-styling">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={patients}
              loading={loading}
              pagination={{
                current: page,
                total: total,
                pageSize: 10,
                onChange: (p) => {
                  runSearch(p, search);
                },
                showSizeChanger: false,
              }}
              locale={{
                emptyText: (
                  <div className="d-flex flex-column align-items-center">
                    <em className="text-muted my-5 text-center">
                      Please search for a patient by <strong>last name</strong>,{" "}
                      <strong>first name</strong>, or <strong>middle name</strong>{" "}
                      to display the list of patients.
                    </em>
                  </div>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* Laboratory Confirmation Modal */}
      <ConfirmModal
        show={showLabModal}
        onHide={() => {
          setShowLabModal(false);
          setSelectedPatient(null);
        }}
        onConfirm={async () => {
          setLabLoading(true);
          try {
            const response = await queueToCSR(
              queueInfo.id,
              selectedPatient?.id,
              queueInfo.queue_no,
              null,
              selectedPatient?.master_patient_id
            );
            
            if (response?.data) {
              // Call markQueueDone after queueToCSR
              await markQueueDone(queueInfo.id);
              
              toast.success("Patient queued to Laboratory successfully");
              setShowLabModal(false);
              setSelectedPatient(null);
              // Navigate back to dashboard
              navigate("/dashboard");
            } else {
              toast.error(response?.error?.data?.message || "Failed to queue patient to Laboratory");
            }
          } catch (error) {
            toast.error("Error queuing patient to Laboratory");
          }
          setLabLoading(false);
        }}
        message="Are you sure you want to queue this patient for Laboratory?"
        caption="This action cannot be undone."
        loading={labLoading}
      />

      {/* ATC Modal - Commented out per client request (ATC not needed) */}
      {/* <Modal
        show={showAtcModal}
        onHide={() => setShowAtcModal(false)}
        backdrop="static"
        keyboard={false}
        size="md"
        centered
      >
        <Modal.Header closeButton className="px-4 pt-4">
        </Modal.Header>
        <Modal.Body className="px-4">
          <div className="row mb-3">
            <div className="col-12">
              <label className="form-label">Authorization Transaction Code (ATC)</label>
              <Input
                placeholder="Enter ATC"
                value={atc}
                onChange={(e) => setAtc(e.target.value)}
                size="middle"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="px-4 pb-4">
          <BtnDelete
            label="Cancel"
            size="sm"
            onClick={() => setShowAtcModal(false)}
            isClicked={atcLoading}
          />
          <BtnSave
            label="Submit"
            size="sm"
            onClick={async () => {
              setAtcLoading(true);
              try {
                let response = null;
                if (atc) {
                  response = await checkPhilhealthATC(atc);
                }
                
                if (!atc || (response && response.status === 200)) {
                  toast.success("ATC verified successfully");
                  setShowAtcModal(false);
                  
                  navigate(`/patients/vitals/${selectedPatient.id}`, {
                    state: {
                      fromConsult: true,
                      queueId: queueInfo.id,
                      queueNo: queueInfo.queue_no,
                      patient_sex: selectedPatient.sex?.toUpperCase() || "",
                      months: getAgeInMonths(selectedPatient.birthdate),
                      atc: atc,
                    },
                  });
                } else {
                  toast.error(response?.data?.message || "Failed to verify ATC");
                }
              } catch (error) {
                toast.error("Error verifying ATC");
              }
              setAtcLoading(false);
            }}
            isClicked={atcLoading}
          />
        </Modal.Footer>
      </Modal> */}
    </div>
  );
}

export default ConsultPatientSearch;
